<template lang="pug">
div
  div(v-if="loading")
    v-skeleton-loader.mb-2(type="list-item-two-line")
    v-skeleton-loader(type="list-item-three-line@3")
  div(v-else)
    stat-card.mb-2(:title="title" :value="getValueByType(todayValue)")
    v-card.rounded-lg(color="white" flat min-height="315")
      v-tooltip(top color="#333333" content-class="black tooltip-top-arrow" :disabled="!isSample")
        template(#activator="{ on, attrs }")
          div(v-bind="attrs" v-on="on")
            .pa-6
              .d-flex.mb-4
                .text-sm.font-medium.neutral500--text.flex-grow-1 {{ averageTitle }}
                .text-xxs.sample-tag.ml-2(v-if="isSample") SAMPLE DATA

              v-card(flat)
                .text-lg.font-medium {{ info }}
                div.mt-6
                  .text-g8 {{ todayValueByType }} 
                    span.neutral500--text {{ chartSubject }}
                  div.mt-2
                    div.d-flex.align-center.barchart.px-3(:style="todayOptions.width ? 'position: absolute;' : ''")
                      div.text-sm.font-semibold {{ isToday ? 'Today' : 'Current' }}
                    div.d-flex.align-center.barchart.px-3(v-if="todayOptions.width" :style="`background-color: ${todayOptions.color}; width: ${todayOptions.width}%; position: relative; overflow: hidden;`")
                      div.text-sm.font-semibold(:style="`color: ${todayOptions.textColor}`") {{ isToday ? 'Today' : 'Current' }}
                div.mt-3
                  .text-g8 {{ yesterdayValueByType }} 
                    span.neutral500--text {{ chartSubject }}
                  div.d-flex.align-center.barchart.px-3.mt-2(:style="`background-color: ${yesterdayOptions.color}; width: ${yesterdayOptions.width}%`") 
                    div.text-sm.font-semibold(:style="`color: ${yesterdayOptions.textColor}`") {{ isToday ? 'Yesterday' : 'Previous' }}

                v-overlay.primary--text.text-center(v-if="isEmptyData && !isSample" absolute color="white" opacity="0.9" z-index="2")
                  v-icon.mb-2(color="primary" size=20) $yzi-bar-chart-10
                  .h8.mb-1 No data available
                  .text-sm There’s no data available yet for this chart
        .py-2.px-0
          .h9.mb-1 This data is only available in premium plan
          .text-xs.mb-2 Find out by upgrading now
          y-button(label="Upgrade now" size="sm" outlined color="white")
</template>

<script>
import { formatMinutes } from '@/utils'
import StatCard from './StatCard.vue'

export default {
  name: 'ComparisonCard',
  components: { StatCard },
  props: {
    title: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: true,
    },
    subject: {
      type: String,
      required: true
    },
    chartSubject: {
      type: String,
      default: null
    },
    averageTitle: {
      type: String,
      required: true
    },
    todayValue: {
      type: Number,
      required: true
    },
    yesterdayValue: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      default: ''
    },
    category: {
      type: String,
    },
    isToday: {
      type: Boolean,
      default: false,
    },
    period: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    isSample: false,
  }),
  computed: {
    info () {
      const differences = this.todayValue - this.yesterdayValue
      const positiveDifferences = Math.abs(differences)
      if (this.isToday) {
        if (differences > 0) {
          return `${this.subject} is up from yesterday`
        } else if (differences < 0) {
          return `${this.subject} down from yesterday`
        } else {
          return `${this.subject} equal to yesterday`
        }
      }
      let formattedDifferences = differences
      let formattedPositiveDifferences = positiveDifferences
      if (this.type === 'price') {
        formattedDifferences = this.$options.filters.displayPriceNoCent(differences, this.$currency)
        formattedPositiveDifferences = this.$options.filters.displayPriceNoCent(positiveDifferences, this.$currency)
      }
      if (differences > 0) {
        return `${this.subject} up ${formattedDifferences} ${this.periodText}`
      } else if (formattedDifferences < 0) {
        return `${this.subject} down ${formattedPositiveDifferences} ${this.periodText}`
      } else {
        return `${this.subject} maintained ${this.periodText}`
      }
    },
    periodText () {
      switch (this.period?.id) {
        case 'lastSeven':
          return 'in the last 7 days'
        case 'lastThirty':
          return 'in the last 30 days'
        case 'lastNinety':
          return 'in the last 90 days'
        case 'thisWeek':
          return 'this week'
        case 'thisMonth':
          return 'this month'
        case 'thisYear':
          return 'this year'
        case 'lastWeek':
          return 'this week'
        case 'lastMonth':
          return 'this month'
        case 'custom':
          return ''
        default:
          return ''
      }
    },
    isEmptyData () {
      return this.todayValue === 0 && this.yesterdayValue === 0
    },
    todayValueByType () {
      return this.getValueByType(this.isSample ? 100 : this.todayValue)
    },
    yesterdayValueByType () {
      return this.getValueByType(this.isSample ? 50 : this.yesterdayValue)
    },
    todayOptions () {
      let higherValue = this.todayValue
      if (this.yesterdayValue > this.todayValue) {
        higherValue = this.yesterdayValue 
      }
      let data = {
        textColor: '#FFFFFF'
      }
      if (this.isSample) {
        data.color = '#C1C1C1',
        data.width = 100
      } else if (this.isEmptyData){
        data.color = '#EEEEEE',
        data.width = 50
      } else {
        data.color = this.getColorByCategory(this.category),
        data.width = Math.round((this.todayValue / higherValue) * 100)
      }
      return data
      // const max = Math.max(this.todayValue || 0, this.yesterdayValue || 0)
      // const period = this.isToday ? 'Today' : 'Current'
      // if (this.isSample) {
      //   return this.buildGraphOptions(period, '#C1C1C1', 100, 100)
      // } else if (this.isEmptyData){
      //   return this.buildGraphOptions(period, '#EEEEEE', 1, 2)
      // } else {
      //   return this.buildGraphOptions(period, this.getColorByCategory(this.category), this.todayValue, max)
      // }
    },
    yesterdayOptions () {
      let higherValue = this.todayValue
      if (this.yesterdayValue > this.todayValue) {
        higherValue = this.yesterdayValue 
      }
      let data = {
        textColor: '#FFFFFF',
        color: '#EEEEEE'
      }
      if (this.isSample) {
        data.width = 50
      } else if (this.isEmptyData){
        data.width = 50
      } else {
        data.textColor = '#333333',
        data.width = Math.round((this.yesterdayValue / higherValue) * 100)
      }
      return data
      // const max = Math.max(this.todayValue || 0, this.yesterdayValue || 0)
      // const period = this.isToday ? 'Yesterday' : 'Previous'
      // if (this.isSample) {
      //   return this.buildGraphOptions(period, '#EEEEEE', 50, 100)
      // } else if (this.isEmptyData){
      //   return this.buildGraphOptions(period, '#EEEEEE', 1, 2)
      // } else {
      //   return this.buildGraphOptions(period, '#EEEEEE', this.yesterdayValue, max, '#333333')
      // }
    }
  },
  methods: {
    getValueByType (value) {
      switch (this.type) {
        case 'price':
          return this.$options.filters.displayPriceNoCent(value)
        case 'time':
          return formatMinutes(value)
        default:
          return value
      }
    },
    getColorByCategory (color) {
      switch (color) {
        case 'appointment':
          return '#E91E63'
        case 'in-clinic':
          return '#00A699'
        case 'patients':
          return '#5856d6'
        default:
          return '#007AFF'
      }
    },
    buildGraphOptions (title, color, value, maxValue, labelColor) {
      const option = {
        series: [{
          data: [value],
          name: '',
        }],
        colors: [color],
        chart: {
          type: 'bar',
          toolbar: { show: false },
          animations: {
            enabled: false
          },
          offsetX: -26,
          offsetY: -32
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: true,
            dataLabels: {
              position: 'bottom'
            },
          }
        },
        dataLabels: {
          enabled: true,
          textAnchor: 'start',
          offsetX: -4,
          formatter: () => {
            return title;
          },
          style: {
            fontSize: '14px',
            fontFamily: 'Inter',
            fontWeight: '500'
          }
        },
        legend: {
          show: false
        },
        grid: {
          show: false,
        },
        xaxis: {
          max: maxValue,
          categories: [''],
          labels: {
            show: false
          },
          axisTicks: {
            show: false
          },
          axisBorder: {
            show: false
          },
          tooltip: {
            enabled: false
          }
        },
        yaxis: {
          labels: {
            show: false
          },
          axisBorder: {
            show: false
          },
        },
        tooltip: {
          enabled: false,
        },
        states: {
          hover: {
            filter: {
              type: 'none'
            }
          },
          active: {
            filter: {
              type: 'none'
            }
          }
        }
      }
      if (labelColor) {
        option.dataLabels.style.colors = [labelColor]
      }
      return option
    }
  }
}
</script>

<style lang="scss" scoped>
.chart-container {
  // height: 70px !important;
  // overflow: hidden;
  // margin-top: -60px;
}
.sample-tag {
  padding: 1px 4px;
  background-color: #EB7100;
  color: white;
  border-radius: 4px;
}
.barchart {
  height: 40px;
  border-radius: 4px;
}
</style>